import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Calendar, Clock, MapPin, Users, Heart, Ticket } from "lucide-react";
import { api, currencySymbol, logError, PLACEHOLDER_IMG } from "@/lib/apiClient";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { ActivityCard } from "@/components/ActivityCard";

const statusStyle = {
  confirmed: "bg-emerald-100 text-emerald-700",
  pending: "bg-amber-100 text-amber-700",
  cancelled: "bg-red-100 text-red-600",
  refunded: "bg-slate-200 text-slate-600",
};

export default function Dashboard() {
  const { t } = useI18n();
  const { user, login } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [favorites, setFavorites] = useState([]);

  const loadBookings = () => api.get("/bookings", { params: { _t: Date.now() } }).then((r) => setBookings(r.data)).catch((e) => logError("bookings load failed", e));
  useEffect(() => {
    if (!user) return;
    loadBookings();
    api.get("/favorites", { params: { _t: Date.now() } }).then((r) => setFavorites(r.data)).catch((e) => logError("favorites load failed", e));
  }, [user]);

  const cancel = async (id) => {
    try { await api.post(`/bookings/${id}/cancel`); toast.success("Booking cancelled"); loadBookings(); }
    catch (e) { toast.error("Could not cancel"); }
  };

  if (!user) {
    return (
      <div className="min-h-[60vh] grid place-items-center text-center px-6">
        <div>
          <h1 className="font-display text-2xl font-semibold mb-3">{t("login_prompt")}</h1>
          <Button data-testid="dashboard-login" onClick={login} className="rounded-full">{t("login")}</Button>
        </div>
      </div>
    );
  }

  const statusLabel = (b) => {
    if (b.status === "cancelled") return t("cancelled");
    if (b.status === "refunded") return t("refunded");
    if (b.payment_status === "cash_on_arrival") return t("pay_on_arrival");
    return b.status === "confirmed" ? t("confirmed") : t("pending");
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex items-center gap-3 mb-6">
        <img src={user.picture} alt="" className="h-12 w-12 rounded-full object-cover border border-black/10" onError={(e) => (e.target.style.display = "none")} />
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Hi, {user.name?.split(" ")[0]} 👋</h1>
          <p className="text-sm text-muted-foreground">{user.email}</p>
        </div>
      </div>

      <Tabs defaultValue="bookings">
        <TabsList>
          <TabsTrigger value="bookings" data-testid="tab-bookings"><Ticket className="h-4 w-4 mr-1.5" /> {t("my_bookings")}</TabsTrigger>
          <TabsTrigger value="favorites" data-testid="tab-favorites"><Heart className="h-4 w-4 mr-1.5" /> {t("favorites")}</TabsTrigger>
        </TabsList>

        <TabsContent value="bookings" className="mt-6">
          {bookings.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground" data-testid="no-bookings">
              {t("no_bookings")} · <Link to="/activities" className="text-primary font-medium">{t("explore")}</Link>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {bookings.map((b) => (
                <div key={b.id} data-testid={`booking-${b.id}`} className="rounded-2xl bg-white border border-black/5 soft-shadow overflow-hidden flex">
                  <img src={b.activity_image || PLACEHOLDER_IMG} alt="" className="w-28 object-cover" />
                  <div className="p-4 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-semibold font-display leading-tight">{b.activity_title}</h3>
                      <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${statusStyle[b.status]}`} data-testid={`booking-status-${b.id}`}>{statusLabel(b)}</span>
                    </div>
                    <div className="mt-2 space-y-1 text-xs text-muted-foreground">
                      <p className="flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> {b.date} <Clock className="h-3.5 w-3.5 ml-1" /> {b.time}</p>
                      <p className="flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> {b.guests} guest(s)</p>
                      <p className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {b.location?.name}</p>
                    </div>
                    <div className="flex items-center justify-between mt-3">
                      <span className="font-display font-bold">{currencySymbol(b.currency)}{b.total_price}</span>
                      {b.status !== "cancelled" && b.status !== "refunded" && (
                        <button data-testid={`cancel-${b.id}`} onClick={() => cancel(b.id)} className="text-xs text-red-500 hover:underline">{t("cancel")}</button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="favorites" className="mt-6">
          {favorites.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">No favorites yet.</div>
          ) : (
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {favorites.map((a, i) => <ActivityCard key={a.id} activity={a} index={i} />)}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
